// WHAT: Widget component for displaying reminder statistics
// WHY:  Provides dashboard overview of upcoming and overdue reminders
// DOMAIN: Core → Dashboard → Widgets → Productivity
// DEPENDENCIES: useReminders hook
// RISKS:
//   - NOTE: Date comparison may have timezone issues
//   - NOTE: Filtering logic may not account for all reminder states
//   - NOTE: No error handling for reminder loading failures

import { Bell, AlertCircle } from "lucide-react";
import { type FC } from "react";

import { Text, Heading, Card, Grid } from "@/components/ui";
import { useReminders } from "@/domains/productivity/reminders/hooks/useReminders";

import { useWidgetConfig } from "../../../hooks/useWidgetConfig";
import { WIDGET_IDS } from "../../../store/widgetRegistry";
import { WidgetContainer } from "../base";

/**
 * WHAT:
 *   Widget component that displays reminder statistics including upcoming
 *   and overdue reminders.
 *
 * WHY:
 *   Enables users to see their reminder status at a glance on the dashboard.
 *
 * BEHAVIOR:
 *   - Fetches reminders from hook
 *   - Filters reminders by due date
 *   - Displays counts for upcoming and overdue reminders
 *   - Shows visual indicators (icons) for each category
 *
 * RISKS:
 *   - NOTE: Date comparison may have timezone issues
 *   - NOTE: Filtering logic may not account for all reminder states
 *   - NOTE: No error handling for reminder loading failures
 *
 * @returns {JSX.Element} Reminders widget component
 *
 */
export const RemindersWidget: FC = () => {
  const { config } = useWidgetConfig(WIDGET_IDS.REMINDERS);
  const { reminders: rawReminders } = useReminders();
  const reminders = rawReminders as {
    dueDate?: Date | number | string;
    done?: boolean;
  }[];

  // NOTE: Filter reminders by due date
  const now = new Date();
  /**
   * WHAT:
   *   Filters reminders that are upcoming (due date in future).
   *
   * WHY:
   *   Separates upcoming reminders from overdue ones.
   */
  const upcoming = reminders.filter((r) => {
    if (!r.dueDate) {
      return false;
    }

    const dueValue = r.dueDate;
    if (
      !(typeof dueValue === "string" || typeof dueValue === "number" || dueValue instanceof Date)
    ) {
      return false;
    }

    const due = new Date(dueValue);
    return due > now;
  });
  /**
   * WHAT:
   *   Filters reminders that are overdue (due date in past and not done).
   *
   * WHY:
   *   Identifies reminders that need attention.
   */
  const overdue = reminders.filter((r) => {
    if (!r.dueDate) {
      return false;
    }

    const dueValue = r.dueDate;
    if (
      !(typeof dueValue === "string" || typeof dueValue === "number" || dueValue instanceof Date)
    ) {
      return false;
    }

    const due = new Date(dueValue);
    return due < now && !r.done;
  });

  return (
    <WidgetContainer
      description="Upcoming & overdue"
      icon={<Bell className="w-5 h-5" />}
      size={config?.size ?? "small"}
      title="Reminders"
      widgetId={WIDGET_IDS.REMINDERS}
    >
      <Grid cols={{ sm: 2 }} gap={3}>
        <Card className="text-center" padding="sm" variant="default">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Bell className="w-4 h-4 text-blue-400" />
            <Heading className="text-xs text-gray-400" level={5}>
              Upcoming
            </Heading>
          </div>
          <Text className="text-2xl font-bold text-white">{upcoming.length}</Text>
        </Card>

        <Card className="text-center" padding="sm" variant="default">
          <div className="flex items-center justify-center gap-2 mb-2">
            <AlertCircle className="w-4 h-4 text-red-400" />
            <Heading className="text-xs text-gray-400" level={5}>
              Overdue
            </Heading>
          </div>
          <Text className="text-2xl font-bold text-white">{overdue.length}</Text>
        </Card>
      </Grid>
    </WidgetContainer>
  );
};
